import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useDropzone } from "react-dropzone";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import * as XLSX from "xlsx";
import axios from "axios";
import { saveAs } from "file-saver";
import { Toaster, toast } from "react-hot-toast";
import JSZip from "jszip"; // <-- Kept for client-side zipping

import "./App.css";
import "react-resizable/css/styles.css";

// --- API URL Logic (Kept for fetching fonts) ---
const API_BASE_URL = ""; // All requests are now relative

const API_FONTS_URL = `${API_BASE_URL}/api/fonts`;
const API_SEND_URL = `${API_BASE_URL}/api/generate-and-send`;

// --- End API URL Logic ---

const COLOR_SWATCHES = [
  "#000000",
  "#FFFFFF",
  "#C67F0E",
  "#0D47A1",
  "#C2185B",
  "#388E3C",
];

const DEFAULT_EMAIL_MESSAGE = `Hi {name},

Congratulations! Your certificate is ready. Please find it attached to this email.

Best regards,
The Certificates Team`;

// --- VULNERABILITY REMOVED ---
// const LOCKED_EMAIL_FIELDS = ... (This constant has been deleted)

const DEFAULT_TEMPLATE_SIZE = { width: 800, height: 600 };
const MIN_LAYOUT_WIDTH = 100;
const MIN_LAYOUT_HEIGHT = 30;
const LAYOUT_STORAGE_KEY = "certificate-designer-layouts";
const MAX_MANUAL_RECIPIENTS = 5;
const DEFAULT_ZOOM_SCALE = 0.35;
const PREVIEW_THUMBNAIL_WIDTH = 300; // Width for grid previews

const stripExtension = (filename = "") =>
  (filename ?? "").toString().replace(/\.[^/.]+$/, "");

const removeControlCharacters = (value = "") =>
  Array.from((value ?? "").toString())
    .map((char) => (char.charCodeAt(0) < 32 ? "" : char))
    .join("");

const sanitizeFileBaseName = (value = "", fallback = "certificate") => {
  const cleaned = removeControlCharacters(value)
    .replace(/[<>:"/\\|?*]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || fallback;
};

const toTitleCase = (value = "") =>
  (value ?? "")
    .toString()
    .split(/\s+/)
    .map((word) =>
      word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : ""
    )
    .join(" ")
    .trim();

const getCellValue = (row = {}, key = "") => {
  if (!row || typeof row !== "object") {
    return "";
  }

  if (Object.prototype.hasOwnProperty.call(row, key)) {
    return row[key];
  }

  const normalizedKey = key.toLowerCase();
  const matchedKey = Object.keys(row).find(
    (column) => column?.toString().toLowerCase() === normalizedKey
  );

  if (matchedKey) {
    return row[matchedKey];
  }

  return "";
};

const createManualRecipient = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  email: "",
});

const CANVAS_TEXT_ALIGN = {
  left: "left",
  center: "center",
  right: "right",
};
const MIN_DYNAMIC_FONT_SIZE = 8;
const FONT_FIT_PADDING = 0.9;
const GOLDEN_BORDER_PADDING = 20;

/**
 * Re-usable function to fit font size within a bounding box.
 * Used by both the full-res and thumbnail renderers.
 */
const fitFontSizeToBox = (
  ctx,
  text,
  fontFamily,
  desiredSize,
  boxWidth,
  boxHeight
) => {
  let size = Math.max(MIN_DYNAMIC_FONT_SIZE, Number(desiredSize) || 0);
  const safeText = text?.toString() || "";
  const iterations = 12;

  for (let i = 0; i < iterations; i++) {
    ctx.font = `${size}px "${fontFamily}"`;
    const metrics = ctx.measureText(safeText);
    const ascent = metrics.actualBoundingBoxAscent || size * 0.8;
    const descent = metrics.actualBoundingBoxDescent || size * 0.2;
    const left = Math.abs(metrics.actualBoundingBoxLeft || 0);
    const right = Math.abs(metrics.actualBoundingBoxRight || 0);
    const width = left + right || metrics.width || 0;
    const height = ascent + descent;

    const allowedWidth =
      Math.max(1, boxWidth - GOLDEN_BORDER_PADDING * 2) * FONT_FIT_PADDING;
    const allowedHeight = Math.max(1, boxHeight) * FONT_FIT_PADDING;

    const widthRatio = width ? allowedWidth / width : 1;
    const heightRatio = height ? allowedHeight / height : 1;
    const ratio = Math.min(widthRatio, heightRatio);

    if (ratio >= 1) {
      break;
    }

    const nextSize = Math.max(
      MIN_DYNAMIC_FONT_SIZE,
      Math.floor(size * Math.max(0.1, ratio))
    );

    if (nextSize >= size) {
      size = Math.max(MIN_DYNAMIC_FONT_SIZE, size - 1);
    } else {
      size = nextSize;
    }

    if (size <= MIN_DYNAMIC_FONT_SIZE) {
      break;
    }
  }

  return size;
};

const createInitialLayout = (templateWidth, templateHeight) => {
  const safeWidth = Math.max(MIN_LAYOUT_WIDTH, Math.round(templateWidth * 0.4));
  const safeHeight = Math.max(
    MIN_LAYOUT_HEIGHT,
    Math.round(templateHeight * 0.08)
  );

  return {
    x: Math.max(0, Math.round((templateWidth - safeWidth) / 2)),
    y: Math.max(0, Math.round(templateHeight * 0.45 - safeHeight / 2)),
    width: Math.min(templateWidth, safeWidth),
    height: Math.min(templateHeight, safeHeight),
    fontSize: 160,
    fontFamily: "Sloop Script Bold One",
    color: "#C67F0E",
    align: "center",
    v_align: "middle",
  };
};

/**
 * Draws a single certificate onto an in-memory canvas at FULL resolution.
 * This is used for the final client-side ZIP generation.
 */
const drawCertificateToCanvas = (
  canvas,
  templateImage,
  layout,
  fullName,
  options = {}
) => {
  const { drawName = true } = options;
  const { width: templateWidth, height: templateHeight } = templateImage;

  // Set canvas size to the template's full resolution
  canvas.width = templateWidth;
  canvas.height = templateHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get 2D context");
  }

  // 1. Draw the template image
  ctx.drawImage(templateImage, 0, 0, templateWidth, templateHeight);

  if (drawName) {
    if (!layout) {
      throw new Error("A layout is required to draw recipient names.");
    }

    // 2. Get layout settings from state
    const { x, y, width, height, fontSize, fontFamily, color, align, v_align } =
      layout;

    const desiredFontSize = Math.max(
      MIN_DYNAMIC_FONT_SIZE,
      Math.round(fontSize) || 0
    );
    const activeFontFamily = fontFamily || "sans-serif";
    const appliedFontSize = fitFontSizeToBox(
      ctx,
      fullName,
      activeFontFamily,
      desiredFontSize,
      width,
      height
    );

    // 3. Apply all text styles
    ctx.font = `${appliedFontSize}px "${activeFontFamily}"`;
    ctx.fillStyle = color || "#000000";
    ctx.textAlign = CANVAS_TEXT_ALIGN[align] || "center";

    // 4. Calculate X anchor point
    const anchorX =
      align === "left" ? x : align === "right" ? x + width : x + width / 2;

    // 5. Calculate Y anchor point
    let anchorY;
    if (v_align === "top") {
      ctx.textBaseline = "top";
      anchorY = y;
    } else if (v_align === "bottom") {
      ctx.textBaseline = "bottom";
      anchorY = y + height;
    } else {
      // "middle"
      ctx.textBaseline = "middle";
      anchorY = y + height / 2;
    }

    // 6. Clip to the layout box and draw the text
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.clip();
    ctx.fillText(fullName, anchorX, anchorY);
    ctx.restore();
  }

  // 7. Return a promise that resolves with the image data
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas toBlob failed to create a blob."));
        return;
      }
      resolve(blob);
    }, "image/png");
  });
};

/**
 * [NEW FUNCTION]
 * Draws a single certificate onto an in-memory canvas at THUMBNAIL resolution.
 * This is used for the right-hand preview grid.
 */
const drawCertificateToCanvasThumbnail = (
  canvas,
  templateImage,
  fullLayout,
  fullName,
  options = {}
) => {
  const { drawName = true, thumbnailWidth = PREVIEW_THUMBNAIL_WIDTH } = options;
  const { naturalWidth: templateWidth, naturalHeight: templateHeight } =
    templateImage;

  // Calculate scaled dimensions
  const scaleRatio = thumbnailWidth / templateWidth;
  const thumbnailHeight = templateHeight * scaleRatio;

  // Set canvas size to the thumbnail's resolution
  canvas.width = thumbnailWidth;
  canvas.height = thumbnailHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get 2D context");
  }

  // 1. Draw the template image (scaled down)
  ctx.drawImage(templateImage, 0, 0, thumbnailWidth, thumbnailHeight);

  if (drawName) {
    if (!fullLayout) {
      throw new Error("A layout is required to draw recipient names.");
    }

    // 2. Scale the layout properties
    const scaledLayout = {
      x: fullLayout.x * scaleRatio,
      y: fullLayout.y * scaleRatio,
      width: fullLayout.width * scaleRatio,
      height: fullLayout.height * scaleRatio,
      fontSize: fullLayout.fontSize * scaleRatio,
      fontFamily: fullLayout.fontFamily,
      color: fullLayout.color,
      align: fullLayout.align,
      v_align: fullLayout.v_align,
    };

    // 3. Get layout settings from the scaled layout
    const { x, y, width, height, fontSize, fontFamily, color, align, v_align } =
      scaledLayout;

    const desiredFontSize = Math.max(
      MIN_DYNAMIC_FONT_SIZE,
      Math.round(fontSize) || 0
    );
    const activeFontFamily = fontFamily || "sans-serif";

    // 4. Fit text using scaled dimensions
    const appliedFontSize = fitFontSizeToBox(
      ctx,
      fullName,
      activeFontFamily,
      desiredFontSize,
      width,
      height
    );

    // 5. Apply all text styles
    ctx.font = `${appliedFontSize}px "${activeFontFamily}"`;
    ctx.fillStyle = color || "#000000";
    ctx.textAlign = CANVAS_TEXT_ALIGN[align] || "center";

    // 6. Calculate X anchor point (relative to scaled layout)
    const anchorX =
      align === "left" ? x : align === "right" ? x + width : x + width / 2;

    // 7. Calculate Y anchor point (relative to scaled layout)
    let anchorY;
    if (v_align === "top") {
      ctx.textBaseline = "top";
      anchorY = y;
    } else if (v_align === "bottom") {
      ctx.textBaseline = "bottom";
      anchorY = y + height;
    } else {
      // "middle"
      ctx.textBaseline = "middle";
      anchorY = y + height / 2;
    }

    // 8. Clip to the layout box and draw the text
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.clip();
    ctx.fillText(fullName, anchorX, anchorY);
    ctx.restore();
  }

  // 9. Return a Data URL (JPEG for smaller size)
  return canvas.toDataURL("image/jpeg", 0.75);
};

function App() {
  const [template, setTemplate] = useState(null);
  const [templateURL, setTemplateURL] = useState("");
  const [dataFile, setDataFile] = useState(null);
  const [data, setData] = useState([]);
  const [sheetName, setSheetName] = useState("");
  const [fontOptions, setFontOptions] = useState([]);

  const [layout, setLayout] = useState(null);
  const [templateSignature, setTemplateSignature] = useState("");

  const [previewName, setPreviewName] = useState("Your Name Here");
  const [isNameOverlayEnabled, setIsNameOverlayEnabled] = useState(true);
  const [isLayoutLocked, setIsLayoutLocked] = useState(false);

  const [templateSize, setTemplateSize] = useState(DEFAULT_TEMPLATE_SIZE);
  const [previewScale, setPreviewScale] = useState(DEFAULT_ZOOM_SCALE);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [lastGenerationInfo, setLastGenerationInfo] = useState(null);
  const [emailDeliveryEnabled, setEmailDeliveryEnabled] = useState(false);

  // --- MODIFICATION: Updated initial state ---
  const [emailSettings, setEmailSettings] = useState({
    service: "gmail",
    senderName: "Research Orbit", // Default, but now editable
    email: "team.researchorbit@gmail.com", // Default, but now editable
    password: "", // CRITICAL: Password is now empty
    subject: "Your Certificate is Ready!",
    template: DEFAULT_EMAIL_MESSAGE,
  });
  // --- END MODIFICATION ---

  const [isSending, setIsSending] = useState(false);
  const [emailSummary, setEmailSummary] = useState(null);
  const [manualRecipients, setManualRecipients] = useState([
    createManualRecipient(),
  ]);
  const [isManualGenerating, setIsManualGenerating] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [fontsReady, setFontsReady] = useState(false);
  // --- NEW STATE FOR PREVIEW GRID ---
  const [previewImages, setPreviewImages] = useState([]);
  const [isPreviewGridLoading, setIsPreviewGridLoading] = useState(false);
  const previewGridCanvasRef = useRef(null); // Canvas for generating thumbnails
  const templateImageRef = useRef(null); // Ref to hold the loaded template Image object
  const isPreviewFromData = useMemo(() => {
    if (!data.length) return false;
    return data.some((row) => row.Name === previewName);
  }, [data, previewName]);

  const emailReadyRows = useMemo(() => {
    if (!data.length) return [];
    return data.filter((row) => {
      const value = row?.Email?.toString().trim();
      return value && value.includes("@");
    });
  }, [data]);

  const manualReadyRecipients = useMemo(() => {
    return manualRecipients.filter((recipient) => {
      const name = recipient?.name?.toString().trim();
      const email = recipient?.email?.toString().trim();
      return !!name && !!email && email.includes("@");
    });
  }, [manualRecipients]);

  const totalReadyRecipients =
    manualReadyRecipients.length + emailReadyRows.length;
  const hasExcelRecipients = emailReadyRows.length > 0;
  const layoutIsRequired = isNameOverlayEnabled;
  const layoutReady = !layoutIsRequired || (!!layout && isLayoutLocked);
  const templateAssetsReady = !!template && layoutReady;
  const excelDataReady = hasExcelRecipients ? !!dataFile : true;
  const manualRecipientLimitReached =
    manualRecipients.length >= MAX_MANUAL_RECIPIENTS;
  const previewNameIsValid = !!previewName && previewName !== "Your Name Here";

  const canAttemptEmailSend =
    emailDeliveryEnabled &&
    totalReadyRecipients > 0 &&
    (!includeAttachments || templateAssetsReady) &&
    excelDataReady &&
    emailSettings.service.trim().length > 0 &&
    emailSettings.email.trim().length > 0 &&
    emailSettings.password.trim().length > 0 &&
    emailSettings.subject.trim().length > 0 &&
    emailSettings.template.trim().length > 0 &&
    !isSending &&
    !isLoading &&
    !isPreviewLoading;

  const sendButtonLabel = isSending
    ? "Sending..."
    : totalReadyRecipients
    ? `Send ${totalReadyRecipients} Email${
        totalReadyRecipients === 1 ? "" : "s"
      }`
    : "Generate & Send Emails";

  const draggableRef = useRef(null);
  const textMeasureContextRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const templateNaturalSizeRef = useRef(DEFAULT_TEMPLATE_SIZE);
  const savedLayoutsRef = useRef({});
  const templateNaturalWidth =
    templateNaturalSizeRef.current.width || DEFAULT_TEMPLATE_SIZE.width;
  const templateNaturalHeight =
    templateNaturalSizeRef.current.height || DEFAULT_TEMPLATE_SIZE.height;
  const layoutWidth = layout?.width || MIN_LAYOUT_WIDTH;
  const layoutHeight = layout?.height || MIN_LAYOUT_HEIGHT;
  const maxXForInput = Math.max(0, templateNaturalWidth - layoutWidth);
  const maxYForInput = Math.max(0, templateNaturalHeight - layoutHeight);

  // Effect: Load Fonts from Server
  useEffect(() => {
    let cancelled = false;

    async function loadFonts() {
      try {
        const response = await axios.get(API_FONTS_URL);
        const fonts = response.data || [];

        if (fonts.length === 0) {
          toast.error(
            "No fonts loaded from server. Check server 'fonts' folder."
          );
          if (!cancelled) setFontsReady(true);
          return;
        }

        setFontOptions(fonts);

        const styleElId = "dynamic-font-faces";
        let styleEl = document.getElementById(styleElId);
        if (!styleEl) {
          styleEl = document.createElement("style");
          styleEl.id = styleElId;
          document.head.appendChild(styleEl);
        }

        const fontFaceRules = fonts
          .map((font) => {
            // Use API_BASE_URL to construct the correct font path
            const fontUrl = `${API_BASE_URL}/fonts/${font.file}`;
            return `
              @font-face {
                font-family: "${font.family}";
                src: url("${fontUrl}");
              }
            `;
          })
          .join("\n");

        styleEl.innerHTML = fontFaceRules;

        // Wait for fonts to be usable
        if (document.fonts?.ready) {
          await document.fonts.ready;
        } else if (document.fonts?.load) {
          await Promise.all(
            fonts.map((font) =>
              document.fonts.load(`16px "${font.family}"`).catch(() => null)
            )
          );
        }

        if (!cancelled) {
          setFontsReady(true);
        }

        console.log("Dynamic fonts loaded into browser.");
      } catch (err) {
        console.error("Failed to fetch fonts:", err);
        toast.error("Failed to load fonts from server. Is it running?");
        if (!cancelled) {
          setFontsReady(true); // Set to true anyway to unlock UI
        }
      }
    }

    loadFonts();

    return () => {
      cancelled = true;
    };
  }, []);

  // Effect: Load Layouts from LocalStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        savedLayoutsRef.current = parsed;
      }
    } catch (err) {
      console.warn("Failed to restore saved layouts:", err);
    }
  }, []);

  // Effect: Save Layout to LocalStorage
  useEffect(() => {
    if (
      !layout ||
      !templateSignature ||
      typeof window === "undefined" ||
      !templateURL
    ) {
      return;
    }

    try {
      const snapshot = { ...layout };
      savedLayoutsRef.current = {
        ...savedLayoutsRef.current,
        [templateSignature]: snapshot,
      };
      window.localStorage.setItem(
        LAYOUT_STORAGE_KEY,
        JSON.stringify(savedLayoutsRef.current)
      );
    } catch (err) {
      console.warn("Failed to persist layout:", err);
    }
  }, [layout, templateSignature, templateURL]);

  // Effect: Update scaled template size when zoom changes
  useEffect(() => {
    if (!templateURL) return; // Don't run if no template

    const { width: naturalWidth, height: naturalHeight } =
      templateNaturalSizeRef.current;
    if (!naturalWidth || !naturalHeight) return;

    setTemplateSize({
      width: Math.round(naturalWidth * previewScale),
      height: Math.round(naturalHeight * previewScale),
    });
  }, [previewScale, templateSignature, templateURL]); // Rerun on zoom or new template

  // Handler: Template Drop
  const onTemplateDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const toastId = toast.loading("Loading template...");

      setTemplate(file);
      setLastGenerationInfo(null);
      setPreviewImages([]); // Clear old previews
      templateImageRef.current = null; // Clear old image ref

      const objectUrl = URL.createObjectURL(file);

      if (templateURL) {
        URL.revokeObjectURL(templateURL);
      }
      setTemplateURL(objectUrl);

      const img = new Image();
      img.onload = () => {
        templateImageRef.current = img; // Save the loaded image object
        const naturalWidth =
          img.naturalWidth || img.width || DEFAULT_TEMPLATE_SIZE.width;
        const naturalHeight =
          img.naturalHeight || img.height || DEFAULT_TEMPLATE_SIZE.height;
        const signature = `${naturalWidth}x${naturalHeight}`;

        templateNaturalSizeRef.current = {
          width: naturalWidth,
          height: naturalHeight,
        };
        setTemplateSignature(signature);

        // Set to default zoom
        setPreviewScale(DEFAULT_ZOOM_SCALE);
        setTemplateSize({
          width: Math.round(naturalWidth * DEFAULT_ZOOM_SCALE),
          height: Math.round(naturalHeight * DEFAULT_ZOOM_SCALE),
        });

        toast.success("Template loaded.", { id: toastId });

        setLayout((prev) => {
          const savedLayout = savedLayoutsRef.current?.[signature];
          if (savedLayout) {
            return { ...savedLayout };
          }

          if (!prev) {
            return createInitialLayout(naturalWidth, naturalHeight);
          }

          const safeWidth = Math.min(naturalWidth, prev.width);
          const safeHeight = Math.min(naturalHeight, prev.height);
          const maxX = Math.max(0, naturalWidth - safeWidth);
          const maxY = Math.max(0, naturalHeight - safeHeight);

          return {
            ...prev,
            width: safeWidth,
            height: safeHeight,
            x: Math.min(prev.x, maxX),
            y: Math.min(prev.y, maxY),
          };
        });
      };
      img.onerror = () => {
        toast.error("Failed to read template dimensions.", { id: toastId });
        templateImageRef.current = null;
        templateNaturalSizeRef.current = DEFAULT_TEMPLATE_SIZE;
        setPreviewScale(1); // Fallback
        setTemplateSize(DEFAULT_TEMPLATE_SIZE);
      };
      img.src = objectUrl;
    },
    [templateURL]
  );

  // Handler: Data Drop
  const onDataDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setLastGenerationInfo(null);
    setEmailSummary(null);
    setDataFile(file);
    setSheetName("");
    setPreviewImages([]); // Clear old previews

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames?.[0] || "";
        const worksheet = workbook.Sheets[firstSheetName];
        if (!worksheet) {
          toast.error("Excel workbook must contain at least one sheet.");
          setDataFile(null);
          setData([]);
          setSheetName("");
          return;
        }
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error('Excel must have a "Name" column!');
          setDataFile(null);
          setData([]);
          setSheetName("");
          return;
        }

        const namesData = jsonData
          .map((row) => {
            const formatted = toTitleCase(getCellValue(row, "Name") || "");
            if (!formatted) return null;

            const emailValue = (getCellValue(row, "Email") || "")
              .toString()
              .trim();

            return {
              Name: formatted,
              Email: emailValue,
            };
          })
          .filter(Boolean);

        if (!namesData.length) {
          toast.error('Excel must have a "Name" column!');
          setDataFile(null);
          setData([]);
          setSheetName("");
          return;
        }

        const fileBaseName = stripExtension(file?.name || "");
        const normalizedSheetName =
          firstSheetName || fileBaseName || "certificates";
        const sanitizedSheetName = sanitizeFileBaseName(
          normalizedSheetName,
          fileBaseName || "certificates"
        );

        const hasEmails = namesData.some((row) => row.Email);

        setSheetName(sanitizedSheetName);
        setData(namesData);
        setPreviewName(namesData[0]?.Name || "Your Name Here");
        toast.success(`Loaded ${namesData.length} names.`);

        if (!hasEmails) {
          toast(
            "Optional: add an Email column to send certificates directly.",
            {
              icon: "📧",
            }
          );
        }
      } catch (err) {
        toast.error("Failed to parse Excel file: " + err.message);
        setDataFile(null);
        setData([]);
        setSheetName("");
      }
    };
    reader.onerror = (err) => {
      toast.error("Failed to read file: " + err.message);
      setSheetName("");
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const {
    getRootProps: getTemplateProps,
    getInputProps: getTemplateInputProps,
  } = useDropzone({
    onDrop: onTemplateDrop,
    accept: { "image/jpeg": [], "image/png": [] },
    maxFiles: 1,
  });

  const { getRootProps: getDataProps, getInputProps: getDataInputProps } =
    useDropzone({
      onDrop: onDataDrop,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
      },
      maxFiles: 1,
    });

  // --- MODIFICATION: Removed the security check ---
  const handleEmailSettingsChange = useCallback((event) => {
    const { name, value } = event.target;
    setEmailSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);
  // --- END MODIFICATION ---

  const handleManualRecipientChange = useCallback((id, field, value) => {
    setManualRecipients((prev) =>
      prev.map((recipient) =>
        recipient.id === id ? { ...recipient, [field]: value } : recipient
      )
    );
  }, []);

  const addManualRecipient = useCallback(() => {
    setManualRecipients((prev) => {
      if (prev.length >= MAX_MANUAL_RECIPIENTS) return prev;
      return [...prev, createManualRecipient()];
    });
  }, []);

  const removeManualRecipient = useCallback((id) => {
    setManualRecipients((prev) => {
      if (prev.length <= 1) {
        return [createManualRecipient()];
      }
      const next = prev.filter((recipient) => recipient.id !== id);
      return next.length ? next : [createManualRecipient()];
    });
  }, []);

  // Handler: Layout Manual Input
  const handleLayoutChange = useCallback((e) => {
    const { name, value } = e.target;
    setLayout((prev) => {
      if (!prev) return prev;

      const templateWidth =
        templateNaturalSizeRef.current.width || DEFAULT_TEMPLATE_SIZE.width;
      const templateHeight =
        templateNaturalSizeRef.current.height || DEFAULT_TEMPLATE_SIZE.height;

      if (name === "fontSize") {
        return { ...prev, fontSize: Number(value) || 0 };
      }

      if (name === "x") {
        const numeric = Math.round(Number(value) || 0);
        const maxX = Math.max(0, templateWidth - prev.width);
        return {
          ...prev,
          x: Math.min(Math.max(0, numeric), maxX),
        };
      }

      if (name === "y") {
        const numeric = Math.round(Number(value) || 0);
        const maxY = Math.max(0, templateHeight - prev.height);
        return {
          ...prev,
          y: Math.min(Math.max(0, numeric), maxY),
        };
      }

      return { ...prev, [name]: value };
    });
  }, []);

  // Handlers: Alignment Buttons
  const handleAlign = useCallback((align) => {
    setLayout((prev) => {
      if (!prev) return prev;
      return { ...prev, align };
    });
  }, []);

  const handleVAlign = useCallback((v_align) => {
    setLayout((prev) => {
      if (!prev) return prev;
      return { ...prev, v_align };
    });
  }, []);

  // Handler: Draggable Box
  const handleDrag = useCallback(
    (_, data) => {
      setLayout((prev) => {
        if (!prev) return prev;

        const scale = previewScale || 1;
        const templateWidth =
          templateNaturalSizeRef.current.width || DEFAULT_TEMPLATE_SIZE.width;
        const templateHeight =
          templateNaturalSizeRef.current.height || DEFAULT_TEMPLATE_SIZE.height;

        const nextX = Math.round(data.x / scale);
        const nextY = Math.round(data.y / scale);
        const maxX = Math.max(0, templateWidth - prev.width);
        const maxY = Math.max(0, templateHeight - prev.height);

        return {
          ...prev,
          x: Math.min(Math.max(0, nextX), maxX),
          y: Math.min(Math.max(0, nextY), maxY),
        };
      });
    },
    [previewScale]
  );

  // Handler: Resizable Box
  const handleResize = useCallback(
    (_, { size }) => {
      setLayout((prev) => {
        if (!prev) return prev;

        const safeScale = previewScale || 1;
        const templateWidth =
          templateNaturalSizeRef.current.width || DEFAULT_TEMPLATE_SIZE.width;
        const templateHeight =
          templateNaturalSizeRef.current.height || DEFAULT_TEMPLATE_SIZE.height;

        const proposedWidth = Math.round((size.width || 0) / safeScale);
        const proposedHeight = Math.round((size.height || 0) / safeScale);

        const nextWidth = Math.min(
          templateWidth,
          Math.max(MIN_LAYOUT_WIDTH, proposedWidth)
        );
        const nextHeight = Math.min(
          templateHeight,
          Math.max(MIN_LAYOUT_HEIGHT, proposedHeight)
        );

        const maxX = Math.max(0, templateWidth - nextWidth);
        const maxY = Math.max(0, templateHeight - nextHeight);

        return {
          ...prev,
          width: nextWidth,
          height: nextHeight,
          x: Math.min(Math.max(0, prev.x), maxX),
          y: Math.min(Math.max(0, prev.y), maxY),
        };
      });
    },
    [previewScale]
  );

  // Handlers: Preview Name
  const handlePreviewSelect = useCallback((value) => {
    setPreviewName(value ? toTitleCase(value) : "");
  }, []);

  const handlePreviewInput = useCallback((value) => {
    setPreviewName(value ? toTitleCase(value) : "");
  }, []);

  // Handler: Color Swatch
  const handleColorSelect = useCallback((hex) => {
    setLayout((prev) => {
      if (!prev) return prev;
      return { ...prev, color: hex };
    });
  }, []);

  // Effect: Redraw Live Preview Canvas
  useEffect(() => {
    const canvas = previewCanvasRef.current;

    if (!isNameOverlayEnabled) {
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width || 0, canvas.height || 0);
        }
      }
      return;
    }

    if (
      !layout ||
      !previewName ||
      !canvas ||
      typeof document === "undefined" ||
      !fontsReady // Wait for fonts to be ready
    ) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = Math.max(1, Math.round(layout.width || MIN_LAYOUT_WIDTH));
    const height = Math.max(1, Math.round(layout.height || MIN_LAYOUT_HEIGHT));
    const desiredFontSize = Math.max(
      MIN_DYNAMIC_FONT_SIZE,
      Math.round(layout.fontSize) || 0
    );
    const fontFamily = layout.fontFamily || "sans-serif";
    const fontSpec = `${desiredFontSize}px "${fontFamily}"`;
    const pixelRatio =
      typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    let cancelled = false;

    const drawPreview = async () => {
      // Ensure font is loaded before drawing
      if (document.fonts?.load) {
        try {
          await document.fonts.load(fontSpec);
        } catch (err) {
          // Log warning, but attempt to draw anyway
          console.warn("Font preview load warning:", err);
        }
      }

      if (cancelled) return;

      // Scale canvas for high-DPI displays
      canvas.width = Math.max(1, Math.round(width * pixelRatio));
      canvas.height = Math.max(1, Math.round(height * pixelRatio));
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      // Clear and set styles
      ctx.clearRect(0, 0, width, height);

      const appliedFontSize = fitFontSizeToBox(
        ctx,
        previewName,
        fontFamily,
        desiredFontSize,
        width,
        height
      );

      ctx.font = `${appliedFontSize}px "${fontFamily}"`;
      ctx.fillStyle = layout.color || "#000000";
      ctx.textAlign = CANVAS_TEXT_ALIGN[layout.align] || "center";

      // Calculate X anchor point
      let anchorX = width / 2;
      if (layout.align === "left") {
        anchorX = 0;
      } else if (layout.align === "right") {
        anchorX = width;
      }

      // Calculate Y anchor point
      let anchorY;
      if (layout.v_align === "top") {
        ctx.textBaseline = "top";
        anchorY = 0;
      } else if (layout.v_align === "bottom") {
        ctx.textBaseline = "bottom";
        anchorY = height;
      } else {
        // "middle"
        ctx.textBaseline = "middle";
        anchorY = height / 2;
      }

      // Clip to box and draw
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, width, height);
      ctx.clip();
      ctx.fillText(previewName, anchorX, anchorY);
      ctx.restore();
    };

    drawPreview();

    return () => {
      cancelled = true;
    };
  }, [layout, previewName, fontsReady, isNameOverlayEnabled]); // Re-run when fonts are ready

  // Effect: Auto-resize layout box (if not locked)
  useEffect(() => {
    if (
      !templateURL ||
      !layout ||
      isLayoutLocked ||
      !fontsReady // Wait for fonts
    ) {
      return;
    }

    let ctx = textMeasureContextRef.current;
    if (!ctx) {
      const canvas = document.createElement("canvas");
      ctx = canvas.getContext("2d");
      textMeasureContextRef.current = ctx;
    }

    if (!ctx) return;

    // Measure text helper
    const fontSize = Math.max(8, Number(layout.fontSize) || 0);
    ctx.font = `${fontSize}px "${layout.fontFamily}"`;

    const measureName = (text = "") => {
      const safeText = text?.toString() || " ";
      const metrics = ctx.measureText(safeText);
      const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.8;
      const descent = metrics.actualBoundingBoxDescent || fontSize * 0.2;
      return {
        width: metrics.width || 0,
        height: ascent + descent,
      };
    };

    const paddingX = 40; // Horizontal padding
    const paddingY = 24; // Vertical padding
    const templateNaturalWidth =
      templateNaturalSizeRef.current.width || DEFAULT_TEMPLATE_SIZE.width;
    const templateNaturalHeight =
      templateNaturalSizeRef.current.height || DEFAULT_TEMPLATE_SIZE.height;

    // Find max width/height needed for all names
    const previewMetrics = measureName(previewName);
    let maxWidth = previewMetrics.width;
    let maxHeight = previewMetrics.height;

    data.forEach((row) => {
      const text = row?.Name;
      if (!text) return;
      const metrics = measureName(text);
      if (metrics.width > maxWidth) maxWidth = metrics.width;
      if (metrics.height > maxHeight) maxHeight = metrics.height;
    });

    const desiredWidth = Math.ceil(maxWidth + paddingX);
    const desiredHeight = Math.ceil(maxHeight + paddingY);

    // Update layout state
    setLayout((prev) => {
      const nextWidth = Math.min(
        templateNaturalWidth,
        Math.max(MIN_LAYOUT_WIDTH, desiredWidth)
      );
      const nextHeight = Math.min(
        templateNaturalHeight,
        Math.max(MIN_LAYOUT_HEIGHT, desiredHeight)
      );

      // If size hasn't changed, don't update state
      if (nextWidth === prev.width && nextHeight === prev.height) {
        return prev;
      }

      // Recalculate X/Y to keep box centered on change
      const maxX = Math.max(0, templateNaturalWidth - nextWidth);
      const maxY = Math.max(0, templateNaturalHeight - nextHeight);
      const deltaWidth = nextWidth - prev.width;
      const deltaHeight = nextHeight - prev.height;

      let nextX = prev.x;
      if (deltaWidth !== 0) {
        if (prev.align === "center") {
          nextX -= deltaWidth / 2;
        } else if (prev.align === "right") {
          nextX -= deltaWidth;
        }
      }

      let nextY = prev.y;
      if (deltaHeight !== 0) {
        if (prev.v_align === "middle") {
          nextY -= deltaHeight / 2;
        } else if (prev.v_align === "bottom") {
          nextY -= deltaHeight;
        }
      }

      return {
        ...prev,
        width: nextWidth,
        height: nextHeight,
        x: Math.min(Math.max(0, nextX), maxX),
        y: Math.min(Math.max(0, nextY), maxY),
      };
    });
  }, [
    templateURL,
    layout,
    previewName,
    layout?.fontSize, // Rerun if these change
    layout?.fontFamily,
    layout?.align,
    layout?.v_align,
    data,
    isLayoutLocked,
    fontsReady, // Rerun when fonts are ready
  ]);

  // Helpers for styling the resizable box
  const getJustifyContent = () => {
    if (!layout) return "center";
    if (layout.align === "left") return "flex-start";
    if (layout.align === "right") return "flex-end";
    return "center";
  };

  const getAlignItems = () => {
    if (!layout) return "center";
    if (layout.v_align === "top") return "flex-start";
    if (layout.v_align === "bottom") return "flex-end";
    return "center";
  };

  const handleManualGenerate = useCallback(async () => {
    if (!manualReadyRecipients.length) {
      toast.error("Add at least one manual recipient with a name and email.");
      return;
    }
    if (!template) {
      toast.error("Upload a template image first.");
      return;
    }
    if (layoutIsRequired && !layout) {
      toast.error("Position the name on the template before generating.");
      return;
    }
    if (layoutIsRequired && !isLayoutLocked) {
      toast.error("Please lock the layout before generating.");
      return;
    }

    setIsManualGenerating(true);
    const toastId = toast.loading(
      `Generating ${manualReadyRecipients.length} manual certificate${
        manualReadyRecipients.length === 1 ? "" : "s"
      }...`
    );

    try {
      if (!isNameOverlayEnabled) {
        const downloadName = template?.name || "certificate.png";
        saveAs(template, downloadName);
        toast.success(`Downloading ${downloadName}`, { id: toastId });
        return;
      }

      const shouldDrawName = isNameOverlayEnabled;
      const canvas = document.createElement("canvas");
      const templateObjectUrl = URL.createObjectURL(template);
      const templateImage = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) =>
          reject(new Error("Failed to load template image: " + err));
        img.src = templateObjectUrl;
      });
      URL.revokeObjectURL(templateObjectUrl);

      if (manualReadyRecipients.length === 1) {
        const fullName = toTitleCase(
          manualReadyRecipients[0].name?.toString().trim() || ""
        );
        const pngBlob = await drawCertificateToCanvas(
          canvas,
          templateImage,
          layout,
          fullName,
          { drawName: shouldDrawName }
        );
        const downloadName = `${sanitizeFileBaseName(
          fullName,
          "certificate"
        )}.png`;
        saveAs(pngBlob, downloadName);
        toast.success(`Downloading ${downloadName}`, { id: toastId });
      } else {
        const zip = new JSZip();
        const nameCounter = {};
        for (const recipient of manualReadyRecipients) {
          const fullName = toTitleCase(recipient.name?.toString().trim() || "");
          const pngBlob = await drawCertificateToCanvas(
            canvas,
            templateImage,
            layout,
            fullName,
            { drawName: shouldDrawName }
          );
          const safeName = sanitizeFileBaseName(fullName, "certificate");
          nameCounter[safeName] = (nameCounter[safeName] || 0) + 1;
          const uniqueName =
            nameCounter[safeName] > 1
              ? `${safeName}-${nameCounter[safeName]}`
              : safeName;
          zip.file(`${uniqueName}.png`, pngBlob);
        }
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const downloadName = `manual-certificates-${Date.now()}.zip`;
        saveAs(zipBlob, downloadName);
        toast.success(`Downloading ${downloadName}`, { id: toastId });
      }
    } catch (error) {
      console.error("Manual generation failed:", error);
      toast.error(
        "Manual generation failed: " + (error.message || "Unknown error"),
        { id: toastId }
      );
    } finally {
      setIsManualGenerating(false);
    }
  }, [
    manualReadyRecipients,
    template,
    layout,
    isLayoutLocked,
    layoutIsRequired,
    isNameOverlayEnabled,
  ]);

  // --- REPLACED handleGenerate with client-side version ---
  const handleGenerate = async () => {
    if (!template) {
      toast.error("Please upload both a template and a data file.");
      return;
    }
    if (layoutIsRequired && !layout) {
      toast.error(
        "Please position the name on the template before generating."
      );
      return;
    }
    if (layoutIsRequired && !isLayoutLocked) {
      toast.error("Please lock the layout before generating.");
      return;
    }

    setIsLoading(true);
    const toastId = toast.loading(
      `Generating ${data.length} certificates... This may take a moment.`
    );

    try {
      if (!isNameOverlayEnabled) {
        const downloadName = template?.name || "certificate.png";
        saveAs(template, downloadName);
        setLastGenerationInfo({
          count: data.length || 0,
          fileName: downloadName,
          timestamp: new Date().toLocaleString(),
        });
        toast.success(`Downloading ${downloadName}`, { id: toastId });
        return;
      }

      const shouldDrawName = isNameOverlayEnabled;
      const zip = new JSZip();

      // 1. Create an in-memory canvas and load the template image
      const canvas = document.createElement("canvas");
      const templateObjectUrl = URL.createObjectURL(template);
      const templateImage = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) =>
          reject(new Error("Failed to load template image: " + err));
        img.src = templateObjectUrl;
      });
      URL.revokeObjectURL(templateObjectUrl); // Clean up object URL

      const nameCounter = {}; // To handle duplicate names

      // 2. Loop through each name from your 'data' state
      for (const row of data) {
        const fullName = row.Name;
        if (!fullName) continue;

        // Draw the certificate onto the in-memory canvas
        const pngBlob = await drawCertificateToCanvas(
          canvas,
          templateImage,
          layout,
          fullName,
          { drawName: shouldDrawName }
        );

        // Sanitize filename
        const safeName = sanitizeFileBaseName(fullName, "certificate");

        nameCounter[safeName] = (nameCounter[safeName] || 0) + 1;
        const uniqueName =
          nameCounter[safeName] > 1
            ? `${safeName}-${nameCounter[safeName]}`
            : safeName;

        // Add the new PNG blob to the zip file
        zip.file(`${uniqueName}.png`, pngBlob);
      }

      // 3. Generate the final zip file and save it
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const fileBaseName = dataFile?.name ? stripExtension(dataFile.name) : "";
      const fallbackBaseName = fileBaseName || `certificates-${Date.now()}`;
      const zipBaseName = sanitizeFileBaseName(
        sheetName || fileBaseName,
        fallbackBaseName
      );
      const downloadName = `${zipBaseName}.zip`;

      saveAs(zipBlob, downloadName);

      setLastGenerationInfo({
        count: data.length || 0,
        fileName: downloadName,
        timestamp: new Date().toLocaleString(),
      });

      toast.success(`Downloading ${downloadName}`, { id: toastId });
    } catch (err) {
      console.error("Client-side generation failed:", err);
      toast.error("Generation failed: " + (err.message || "Unknown error"), {
        id: toastId,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateAndSend = async () => {
    if (isSending) return;

    if (!emailDeliveryEnabled) {
      toast.error("Enable email delivery before sending.");
      return;
    }

    const manualTargets = manualReadyRecipients.map((recipient) => ({
      name: toTitleCase(recipient.name?.toString().trim() || ""),
      email: recipient.email?.toString().trim(),
    }));

    const totalRecipients = manualTargets.length + emailReadyRows.length;

    if (!totalRecipients) {
      toast.error(
        "Add at least one recipient via your Excel sheet or the manual section."
      );
      return;
    }

    if (includeAttachments) {
      if (!template) {
        toast.error("Upload a template image before attaching certificates.");
        return;
      }
      if (layoutIsRequired && !layout) {
        toast.error("Position the layout before sending attachments.");
        return;
      }
      if (layoutIsRequired && !isLayoutLocked) {
        toast.error("Please lock the layout before sending attachments.");
        return;
      }
    }

    if (emailReadyRows.length && !dataFile) {
      toast.error("Re-upload the Excel file to reach spreadsheet recipients.");
      return;
    }

    const service = emailSettings.service.trim();
    const senderEmail = emailSettings.email.trim();
    const password = emailSettings.password.trim();
    const subject = emailSettings.subject.trim();
    const templateMessage = emailSettings.template.trim();

    if (!service) {
      toast.error("Enter the email service (e.g., gmail, outlook).");
      return;
    }
    if (!senderEmail) {
      toast.error("Enter the sender email address.");
      return;
    }
    if (!password) {
      toast.error("Enter the email app password.");
      return;
    }
    if (!subject) {
      toast.error("Add an email subject line.");
      return;
    }
    if (!templateMessage) {
      toast.error("Add the message template that includes {name}.");
      return;
    }

    setIsSending(true);
    setEmailSummary(null);

    const emailNoun = `${isNameOverlayEnabled ? "personalized " : ""}email${
      totalRecipients === 1 ? "" : "s"
    }`;
    const toastId = toast.loading(`Sending ${totalRecipients} ${emailNoun}...`);

    try {
      const formData = new FormData();
      if (includeAttachments && template) {
        formData.append("templateImage", template);
      }
      if (emailReadyRows.length && dataFile) {
        formData.append("dataFile", dataFile);
      }
      if (includeAttachments && layout && isNameOverlayEnabled) {
        formData.append("layout", JSON.stringify(layout));
      }
      formData.append("emailService", service);
      formData.append("emailUser", senderEmail);
      formData.append("emailPass", password);
      formData.append("senderName", emailSettings.senderName || "");
      formData.append("emailSubject", subject);
      formData.append("emailTemplate", templateMessage);
      formData.append(
        "includeAttachments",
        includeAttachments ? "true" : "false"
      );
      formData.append(
        "personalizeWithNames",
        isNameOverlayEnabled ? "true" : "false"
      );
      formData.append("manualRecipients", JSON.stringify(manualTargets));

      const response = await axios.post(API_SEND_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const payload = response?.data || {};
      setEmailSummary({
        timestamp: new Date().toLocaleString(),
        ...payload,
      });

      toast.success(payload?.message || "Emails sent successfully.", {
        id: toastId,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to send certificates.";
      toast.error(message, { id: toastId });
    } finally {
      setIsSending(false);
    }
  };

  // --- REPLACED handleDownloadPreview with client-side version ---
  const handleDownloadPreview = async () => {
    if (!template) {
      toast.error("Please upload a template first.");
      return;
    }
    if (layoutIsRequired && !layout) {
      toast.error("Please position the name on the template.");
      return;
    }
    if (layoutIsRequired && !isLayoutLocked) {
      toast.error("Please lock the layout before generating.");
      return;
    }
    if (layoutIsRequired && !previewNameIsValid) {
      toast.error("Please enter a valid name to preview and download.");
      return;
    }

    setIsPreviewLoading(true);
    const baseTemplateName = template?.name || "certificate.png";
    const previewLabel =
      layoutIsRequired && previewNameIsValid
        ? previewName
        : stripExtension(baseTemplateName) || "Shared Certificate";
    const toastId = toast.loading(
      isNameOverlayEnabled
        ? `Generating preview for ${previewLabel}...`
        : `Preparing ${baseTemplateName}...`
    );

    try {
      if (!isNameOverlayEnabled) {
        saveAs(template, baseTemplateName);
        toast.success(`Downloading ${baseTemplateName}`, { id: toastId });
        return;
      }

      // 1. Create an in-memory canvas and load the template image
      const canvas = document.createElement("canvas");
      const templateObjectUrl = URL.createObjectURL(template);
      const templateImage = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) =>
          reject(new Error("Failed to load template image: " + err));
        img.src = templateObjectUrl;
      });
      URL.revokeObjectURL(templateObjectUrl); // Clean up

      // 2. Draw the single certificate
      const pngBlob = await drawCertificateToCanvas(
        canvas,
        templateImage,
        layout,
        previewLabel,
        { drawName: isNameOverlayEnabled }
      );

      // 3. Save the single PNG
      const safeName = sanitizeFileBaseName(previewLabel, "certificate");

      const downloadName = `${safeName}.png`;

      saveAs(pngBlob, downloadName);

      toast.success(`Downloading ${downloadName}`, { id: toastId });
    } catch (err) {
      console.error("Client-side preview download failed:", err);
      toast.error("Preview failed: " + (err.message || "Unknown error"), {
        id: toastId,
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  /**
   * [NEW HANDLER]
   * Generates all thumbnails for the right-hand preview grid.
   */
  const handleGeneratePreviews = async () => {
    if (!data.length || !templateImageRef.current || !layout) {
      toast.error("Missing template, data, or layout.");
      return;
    }

    setIsPreviewGridLoading(true);
    setPreviewImages([]);
    const toastId = toast.loading(
      `Generating ${data.length} previews...`,
      { duration: 10000 } // Keep it open
    );

    try {
      // Get or create the in-memory canvas for generating thumbnails
      if (!previewGridCanvasRef.current) {
        previewGridCanvasRef.current = document.createElement("canvas");
      }
      const canvas = previewGridCanvasRef.current;
      const templateImage = templateImageRef.current;
      const generatedImages = [];
      const drawName = isNameOverlayEnabled;

      // Process in chunks to avoid blocking the main thread
      const chunkSize = 25;
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize);

        // Update toast progress
        toast.loading(
          `Generating previews... (${i + chunk.length}/${data.length})`,
          { id: toastId }
        );

        const promises = chunk.map((row) => {
          const fullName = row.Name;
          if (!fullName) return null;

          // Use the new thumbnail drawing function
          const imageDataUrl = drawCertificateToCanvasThumbnail(
            canvas,
            templateImage,
            layout,
            fullName,
            { drawName }
          );

          return {
            name: fullName,
            imageSrc: imageDataUrl,
          };
        });

        const results = (await Promise.all(promises)).filter(Boolean);
        generatedImages.push(...results);

        // Update state with the new batch of images
        setPreviewImages([...generatedImages]);

        // Yield to the main thread
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      toast.success(`Generated ${generatedImages.length} previews.`, {
        id: toastId,
      });
    } catch (err) {
      console.error("Preview grid generation failed:", err);
      toast.error(
        "Preview generation failed: " + (err.message || "Unknown error"),
        { id: toastId }
      );
    } finally {
      setIsPreviewGridLoading(false);
    }
  };

  // --- JSX ---
  return (
    <div className="App">
      <Toaster position="bottom-right" />

      <div className="controls-panel">
        <h2>Design Studio</h2>
        <p className="panel-intro">
          Upload your artwork, decide whether to personalize it, then send or
          download everything in one place.
        </p>

        <div className="control-group">
          <label>1. Upload Template Image</label>
          <div {...getTemplateProps({ className: "dropzone" })}>
            <input {...getTemplateInputProps()} />
            <p>Drag 'n' drop, or click</p>
            {template && <span className="file-name">{template.name}</span>}
          </div>
        </div>

        <div className="control-group">
          <label>2. Upload Data File (.xlsx)</label>
          <div {...getDataProps({ className: "dropzone" })}>
            <input {...getDataInputProps()} />
            <p>Drag 'n' drop, or click</p>
            {dataFile && <span className="file-name">{dataFile.name}</span>}
          </div>
        </div>

        <div className="control-group">
          <label>3. Personalize Certificates</label>
          <div className="personalization-toggle">
            {/* --- THIS IS THE CORRECTED LABEL AND CHECKBOX --- */}
            <label className="toggle-label" htmlFor="nameOverlayToggle">
              <input
                id="nameOverlayToggle"
                type="checkbox"
                checked={isNameOverlayEnabled}
                onChange={(event) => {
                  setIsNameOverlayEnabled(event.target.checked);
                  setPreviewImages([]); // Clear previews on toggle
                }}
              />
              Show each recipient's name on the template
            </label>
            {/* --- END FIX --- */}
            <p className="toggle-hint">
              {isNameOverlayEnabled
                ? "Drag or resize the red box, then lock the layout to prevent accidental changes."
                : "We'll reuse your template exactly as uploaded so everyone gets the same picture."}
            </p>
          </div>

          <label htmlFor="fontFamily">Font Family</label>
          <select
            name="fontFamily"
            value={layout?.fontFamily || ""}
            onChange={handleLayoutChange}
            disabled={
              !layout ||
              isLayoutLocked ||
              fontOptions.length === 0 ||
              !isNameOverlayEnabled
            }
          >
            {fontOptions.length === 0 && <option>Loading fonts...</option>}
            {fontOptions.map((font) => (
              <option key={font.family} value={font.family}>
                {font.family}
              </option>
            ))}
          </select>

          <label htmlFor="fontSize">Font Size (px)</label>
          <input
            type="number"
            name="fontSize"
            min="8"
            value={layout?.fontSize ?? ""}
            onChange={handleLayoutChange}
            disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
          />

          <label htmlFor="color">Font Color</label>
          <input
            type="color"
            name="color"
            value={layout?.color || "#C67F0E"}
            onChange={handleLayoutChange}
            disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
          />
          <div className="color-swatches">
            {COLOR_SWATCHES.map((swatch) => (
              <button
                key={swatch}
                type="button"
                className={`color-swatch ${
                  layout?.color?.toLowerCase() === swatch.toLowerCase()
                    ? "selected"
                    : ""
                }`}
                style={{ backgroundColor: swatch }}
                onClick={() => handleColorSelect(swatch)}
                aria-label={`Set font color to ${swatch}`}
                disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
              />
            ))}
          </div>

          <label htmlFor="positionX">Horizontal Position (px)</label>
          <input
            type="number"
            name="x"
            min="0"
            max={Math.max(0, Math.floor(maxXForInput))}
            value={layout?.x ?? ""}
            onChange={handleLayoutChange}
            disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
          />

          <label htmlFor="positionY">Vertical Position (px)</label>
          <input
            type="number"
            name="y"
            min="0"
            max={Math.max(0, Math.floor(maxYForInput))}
            value={layout?.y ?? ""}
            onChange={handleLayoutChange}
            disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
          />

          <label>Horizontal Alignment</label>
          <div className="font-align">
            <button
              onClick={() => handleAlign("left")}
              className={layout?.align === "left" ? "active" : ""}
              disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
            >
              Left
            </button>
            <button
              onClick={() => handleAlign("center")}
              className={layout?.align === "center" ? "active" : ""}
              disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
            >
              Center
            </button>
            <button
              onClick={() => handleAlign("right")}
              className={layout?.align === "right" ? "active" : ""}
              disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
            >
              Right
            </button>
          </div>

          <label style={{ marginTop: "10px" }}>Vertical Alignment</label>
          <div className="font-align">
            <button
              onClick={() => handleVAlign("top")}
              className={layout?.v_align === "top" ? "active" : ""}
              disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
            >
              Top
            </button>
            <button
              onClick={() => handleVAlign("middle")}
              className={layout?.v_align === "middle" ? "active" : ""}
              disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
            >
              Middle
            </button>
            <button
              onClick={() => handleVAlign("bottom")}
              className={layout?.v_align === "bottom" ? "active" : ""}
              disabled={!layout || isLayoutLocked || !isNameOverlayEnabled}
            >
              Bottom
            </button>
          </div>

          <button
            className={`confirm-layout-button ${
              isLayoutLocked ? "locked" : ""
            }`}
            onClick={() => {
              setIsLayoutLocked(!isLayoutLocked);
              setPreviewImages([]); // Clear previews when locking/unlocking
            }}
            disabled={!template || !layout || !isNameOverlayEnabled}
          >
            {isLayoutLocked ? "Unlock Layout" : "Lock Layout"}
          </button>
          {!isNameOverlayEnabled && (
            <p className="layout-hint">
              Locking the layout is only required when personalization is on.
            </p>
          )}
        </div>

        <div className="control-group">
          <label>4. Preview & Test</label>
          <p className="active-preview">
            {isNameOverlayEnabled ? (
              <>
                Currently previewing: <strong>{previewName || "-"}</strong>
              </>
            ) : (
              <span className="muted-note">
                Personalization is off, so everyone will see the original
                template.
              </span>
            )}
          </p>
          <select
            onChange={(e) => handlePreviewSelect(e.target.value)}
            value={data.length && isPreviewFromData ? previewName : ""}
            disabled={!data.length || !isNameOverlayEnabled}
          >
            <option value="" disabled={!!data.length}>
              {data.length ? "Select a name" : "Load a data file first"}
            </option>
            {data.map((row, i) => (
              <option key={i} value={row.Name}>
                {row.Name}
              </option>
            ))}
          </select>

          <input
            type="text"
            className="preview-input"
            placeholder="Or type any name to preview"
            value={previewName}
            onChange={(e) => handlePreviewInput(e.target.value)}
            disabled={!isNameOverlayEnabled}
          />

          <button
            className="preview-download-button"
            onClick={handleDownloadPreview}
            disabled={
              !template ||
              isPreviewLoading ||
              isLoading ||
              (layoutIsRequired && !layoutReady) ||
              (layoutIsRequired && !previewNameIsValid)
            }
          >
            {isPreviewLoading ? "Generating..." : "Download Preview as PNG"}
          </button>
        </div>

        <div className="control-group">
          <label>5. Quick Fix Recipients (Optional)</label>
          <p className="layout-hint">
            Missing someone? Add up to two names here without touching your
            Excel file.
          </p>
          {manualRecipients.map((recipient) => (
            <div className="manual-recipient-row" key={recipient.id}>
              <input
                type="text"
                placeholder="Full name"
                value={recipient.name}
                onChange={(event) =>
                  handleManualRecipientChange(
                    recipient.id,
                    "name",
                    event.target.value
                  )
                }
              />
              {/* This wrapper div fixes the layout */}
              <div className="manual-recipient-email-row">
                <input
                  type="email"
                  placeholder="Email address"
                  value={recipient.email}
                  onChange={(event) =>
                    handleManualRecipientChange(
                      recipient.id,
                      "email",
                      event.target.value
                    )
                  }
                />
                {manualRecipients.length > 1 && (
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => removeManualRecipient(recipient.id)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="manual-recipient-actions">
            <button
              type="button"
              className="add-manual-button"
              onClick={addManualRecipient}
              disabled={manualRecipientLimitReached}
            >
              + Add another person
            </button>
            <span className="manual-limit-hint">
              Ready: {manualReadyRecipients.length} / {MAX_MANUAL_RECIPIENTS}
            </span>
          </div>
          <p className="layout-hint">
            Manual recipients are included when you send emails below.
          </p>
          <button
            type="button"
            className="manual-generate-button"
            onClick={handleManualGenerate}
            disabled={
              !template ||
              (layoutIsRequired && !layoutReady) ||
              !manualReadyRecipients.length ||
              isManualGenerating ||
              isLoading ||
              isPreviewLoading
            }
          >
            {isManualGenerating
              ? "Generating..."
              : "Generate PNGs for Manual Recipients"}
          </button>
        </div>

        <div className="control-group">
          <label>6. Email Delivery (Optional)</label>
          <p className="layout-hint">
            Personalize your email with <code>{"{name}"}</code> to insert each
            recipient's name automatically.
          </p>
          <label className="email-toggle">
            <input
              type="checkbox"
              checked={emailDeliveryEnabled}
              onChange={(event) => {
                setEmailDeliveryEnabled(event.target.checked);
              }}
            />
            Enable Generate & Send
          </label>
          <div
            className={`email-settings ${
              emailDeliveryEnabled ? "active" : "disabled"
            }`}
          >
            <div className="email-delivery-stats">
              <span>
                Emails detected:{" "}
                <strong>
                  {emailReadyRows.length}/{data.length || 0}
                </strong>
              </span>
              <span>
                Manual quick recipients:{" "}
                <strong>{manualReadyRecipients.length}</strong>
              </span>
              {emailDeliveryEnabled &&
                !emailReadyRows.length &&
                data.length > 0 && (
                  <span className="email-warning">
                    Add an "Email" column to your Excel file.
                  </span>
                )}
            </div>
            <label className="email-toggle attachment-toggle">
              <input
                type="checkbox"
                checked={includeAttachments}
                onChange={(event) =>
                  setIncludeAttachments(event.target.checked)
                }
                disabled={!emailDeliveryEnabled || isSending}
              />
              Attach certificate PNGs to each email
            </label>
            {!includeAttachments && (
              <p className="email-warning">
                Attachments are disabled. We'll only send your message (perfect
                for sharing a link).
              </p>
            )}
            <label htmlFor="emailService">Email Service</label>
            <input
              id="emailService"
              name="service"
              type="text"
              placeholder="gmail, outlook, yahoo..."
              value={emailSettings.service}
              onChange={handleEmailSettingsChange}
              disabled={!emailDeliveryEnabled || isSending}
            />
            {/* --- MODIFICATION: Removed readOnly --- */}
            <label htmlFor="senderName">Sender Name (optional)</label>
            <input
              id="senderName"
              name="senderName"
              type="text"
              placeholder="Your Organization"
              value={emailSettings.senderName}
              onChange={handleEmailSettingsChange}
              disabled={!emailDeliveryEnabled || isSending}
            />
            <label htmlFor="senderEmail">Sender Email Address</label>
            <input
              id="senderEmail"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={emailSettings.email}
              onChange={handleEmailSettingsChange}
              disabled={!emailDeliveryEnabled || isSending}
            />
            <label htmlFor="emailPassword">Email App Password</label>
            <input
              id="emailPassword"
              name="password"
              type="password"
              autoComplete="off"
              placeholder="Enter the app password from your provider"
              value={emailSettings.password}
              onChange={handleEmailSettingsChange}
              disabled={!emailDeliveryEnabled || isSending}
            />
            {/* --- END MODIFICATION --- */}
            <label htmlFor="emailSubject">Email Subject</label>
            <input
              id="emailSubject"
              name="subject"
              type="text"
              placeholder="Your Certificate is Ready!"
              value={emailSettings.subject}
              onChange={handleEmailSettingsChange}
              disabled={!emailDeliveryEnabled || isSending}
            />
            <label htmlFor="emailTemplate">Message Template</label>
            <textarea
              id="emailTemplate"
              name="template"
              placeholder="Hi {name}, ..."
              value={emailSettings.template}
              onChange={handleEmailSettingsChange}
              disabled={!emailDeliveryEnabled || isSending}
            />
            <p className="template-hint">
              Tip: We'll automatically replace <code>{"{name}"}</code> with each
              recipient's name and attach their certificate as a PNG.
            </p>
          </div>
        </div>

        <div className="control-group">
          <label>7. Generate & Deliver</label>
          <div className="generation-actions">
            <button
              className="generate-button"
              onClick={handleGenerate}
              disabled={
                !template ||
                !dataFile ||
                isLoading ||
                isPreviewLoading ||
                (layoutIsRequired && !layoutReady)
              }
            >
              {isLoading
                ? "Generating..."
                : `Generate ${data.length} Certificates`}
            </button>
            <button
              className="send-button"
              onClick={handleGenerateAndSend}
              disabled={!canAttemptEmailSend}
            >
              {sendButtonLabel}
            </button>
          </div>
          {lastGenerationInfo && (
            <div className="generation-summary">
              <p>
                <strong>Last download:</strong> {lastGenerationInfo.timestamp}
              </p>
              <p>
                <strong>Certificates:</strong> {lastGenerationInfo.count}
              </p>
              <p>
                <strong>ZIP Name:</strong>{" "}
                <code>{lastGenerationInfo.fileName || "-"}</code>
              </p>
            </div>
          )}
          {emailSummary && (
            <div className="generation-summary email-summary">
              <p>
                <strong>Last send:</strong> {emailSummary.timestamp}
              </p>
              <p>
                <strong>Delivered:</strong> {emailSummary.successCount || 0} /{" "}
                {emailSummary.attempted || emailReadyRows.length || 0}
              </p>
              <p>
                <strong>Missing Emails:</strong>{" "}
                {emailSummary.missingEmailCount || 0}
              </p>
              {emailSummary.failureCount > 0 && (
                <details>
                  <summary>
                    Failed deliveries ({emailSummary.failureCount})
                  </summary>
                  <ul className="failure-list">
                    {(emailSummary.failures || [])
                      .slice(0, 5)
                      .map((failure, i) => (
                        <li key={`${failure.email}-${i}`}>
                          {failure.name} - {failure.email}: {failure.reason}
                        </li>
                      ))}
                    {emailSummary.failures?.length > 5 && (
                      <li>...and {emailSummary.failures.length - 5} more</li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="editor-panel">
        {templateURL && (
          <div className="preview-zoom-controls">
            <div className="preview-zoom-header">
              <label htmlFor="zoomSlider">
                Zoom: {Math.round(previewScale * 100)}%
              </label>
              <button
                className="preview-zoom-reset"
                onClick={() => setPreviewScale(DEFAULT_ZOOM_SCALE)}
                disabled={previewScale === DEFAULT_ZOOM_SCALE}
              >
                Reset to 35%
              </button>
            </div>
            <input
              id="zoomSlider"
              className="preview-zoom-slider"
              type="range"
              min="0.1"
              max="0.35"
              step="0.01"
              value={previewScale}
              onChange={(e) => setPreviewScale(parseFloat(e.target.value))}
              disabled={!templateURL}
            />
          </div>
        )}

        {templateURL && (
          <div className="preview-pill">
            {isNameOverlayEnabled ? (
              <>
                Previewing: <strong>{previewName || "-"}</strong>
              </>
            ) : (
              <>Name overlay disabled - sharing template as-is.</>
            )}{" "}
            ({templateNaturalWidth}x{templateNaturalHeight}px)
          </div>
        )}
        {templateURL ? (
          layout ? (
            <>
              <div
                className="editor-canvas"
                style={{
                  width: `${templateSize.width}px`,
                  height: `${templateSize.height}px`,
                  backgroundImage: `url(${templateURL})`,
                }}
              >
                {isNameOverlayEnabled && (
                  <Draggable
                    nodeRef={draggableRef}
                    bounds="parent"
                    position={{
                      x: layout.x * previewScale,
                      y: layout.y * previewScale,
                    }}
                    onDrag={handleDrag}
                    onStop={handleDrag}
                    disabled={isLayoutLocked}
                  >
                    <div
                      ref={draggableRef}
                      style={{
                        display: "inline-block",
                      }}
                    >
                      <ResizableBox
                        width={Math.max(1, layout.width * previewScale)}
                        height={Math.max(1, layout.height * previewScale)}
                        onResizeStop={handleResize}
                        minConstraints={[
                          Math.max(1, MIN_LAYOUT_WIDTH * previewScale),
                          Math.max(1, MIN_LAYOUT_HEIGHT * previewScale),
                        ]}
                        maxConstraints={[
                          templateSize.width,
                          templateSize.height,
                        ]}
                        resizeHandles={isLayoutLocked ? [] : ["se"]}
                      >
                        <div
                          className={`draggable-text-box ${
                            isLayoutLocked ? "locked" : ""
                          }`}
                          style={{
                            width: "100%",
                            height: "100%",
                            justifyContent: getJustifyContent(),
                            alignItems: getAlignItems(),
                          }}
                        >
                          <canvas
                            ref={previewCanvasRef}
                            className="preview-text-canvas"
                            aria-label="Certificate name preview"
                          />
                        </div>
                      </ResizableBox>
                    </div>
                  </Draggable>
                )}
              </div>
              {/* --- BUG FIX: This hint is now OUTSIDE the canvas div --- */}
              {!isNameOverlayEnabled && (
                <div className="overlay-disabled-hint">
                  <strong>Personalization off</strong>
                  <span>
                    Everyone will receive this template exactly as shown.
                  </span>
                </div>
              )}
            </>
          ) : (
            <h3>Preparing layout box...</h3>
          )
        ) : (
          <h3>Upload a template to begin designing</h3>
        )}
      </div>

      {/* --- NEW PREVIEW GRID PANEL (Replaces Data Panel) --- */}
      <div className="preview-grid-panel">
        <h2>All Previews ({data.length})</h2>

        {!template ? (
          <p className="data-panel-hint">Upload a template image first.</p>
        ) : !data.length ? (
          <p className="data-panel-hint">
            Upload an Excel data file to see previews.
          </p>
        ) : !isNameOverlayEnabled ? (
          <p className="data-panel-hint">
            Turn on "Personalize Certificates" to generate previews.
          </p>
        ) : !isLayoutLocked ? (
          <p className="data-panel-hint">
            Lock your layout in Step 3 to generate all previews.
          </p>
        ) : isPreviewGridLoading ? (
          <button className="generate-previews-button" disabled>
            Generating Previews...
          </button>
        ) : previewImages.length === 0 ? (
          <>
            <p className="data-panel-hint">
              Ready to see what everyone's certificate will look like?
            </p>
            <button
              className="generate-previews-button"
              onClick={handleGeneratePreviews}
              disabled={
                !layoutReady || !data.length || !templateImageRef.current
              }
            >
              Generate All {data.length} Previews
            </button>
          </>
        ) : (
          <button
            className="generate-previews-button clear"
            onClick={() => setPreviewImages([])}
          >
            Clear Previews
          </button>
        )}

        {previewImages.length > 0 && !isPreviewGridLoading && (
          <p className="data-panel-hint">
            Showing {previewImages.length} previews. Click a name in Step 4 to
            adjust the main preview.
          </p>
        )}

        <div className="preview-grid-container">
          {previewImages.map((img, i) => (
            <div
              key={i}
              className="preview-grid-item"
              onClick={() => handlePreviewSelect(img.name)}
            >
              <img
                src={img.imageSrc}
                alt={img.name}
                width={PREVIEW_THUMBNAIL_WIDTH}
                loading="lazy"
              />
              <p>{img.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default App;
